import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Loader2, ParkingCircle, Plus, Wrench } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AppointmentAPI, BayAPI, TechnicianAPI, extractError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useFormatters, useTenantTz, getTzParts, tzMinutesIntoDay, tzDayKey, tzWallClockToUtc } from "@/lib/format";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

// ---------------- helpers ----------------
//
// Phase 4.6: all time math runs in the TENANT timezone (not the browser's).
// Slot indices are computed from minutes-into-day in tenant tz, never from
// browser-local `Date.getHours()`. See `lib/format.js::tzMinutesIntoDay`.

const SLOT_MIN = 15;
const DAY_START_HOUR = 8;
const DAY_END_HOUR = 20;
const HOURS = DAY_END_HOUR - DAY_START_HOUR;
const SLOTS_PER_HOUR = 60 / SLOT_MIN;
const TOTAL_SLOTS = HOURS * SLOTS_PER_HOUR; // 48
const PX_PER_SLOT = 20; // 20px * 48 = 960px track

function isoDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}
function dayLabelTz(date, timeZone) {
    return new Intl.DateTimeFormat("en-IN", {
        weekday: "short", day: "2-digit", month: "short", year: "numeric",
        timeZone,
    }).format(date);
}
function addDays(date, n) {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
}

// Compute the UTC instant for "00:00 in tenant-tz on the given local day".
// We do this by reading the tenant-tz wall-clock parts for the local Date
// (using formatToParts), then synthesising the YYYY-MM-DDT00:00 string and
// asking the runtime to re-interpret that wall-clock in the tenant tz.
function tzDayBoundsUtc(localDay, timeZone) {
    // First derive the tenant-tz calendar date for whatever 12:00 noon UTC
    // corresponds to on the chosen local day (12:00 noon UTC = same tenant-tz
    // calendar day for any tz between UTC-12 and UTC+12).
    const probe = new Date(Date.UTC(
        localDay.getFullYear(),
        localDay.getMonth(),
        localDay.getDate(),
        12, 0, 0,
    ));
    const p = getTzParts(probe, timeZone);
    const pad = (n) => String(n).padStart(2, "0");
    return `${p.year}-${pad(p.month)}-${pad(p.day)}`;
}

// Minutes from the day's 08:00 start (tenant tz) to the given ISO instant.
// `dayKey` is "YYYY-MM-DD" in tenant tz. Returns a finite number even if the
// instant falls on a different day — negative for before, > 24*60 for after,
// so callers can decide whether to render an "off-band" marker.
function slotsFromTzStart(dayKey, isoInstant, timeZone) {
    const minutes = tzMinutesIntoDay(isoInstant, dayKey, timeZone);
    if (minutes == null) {
        // The instant is on a different tenant-tz calendar day — figure out
        // which direction and return a slot count outside the visible band.
        const p = getTzParts(isoInstant, timeZone);
        if (!p) return null;
        const pad = (n) => String(n).padStart(2, "0");
        const otherKey = `${p.year}-${pad(p.month)}-${pad(p.day)}`;
        return otherKey < dayKey ? -10_000 : 10_000;
    }
    return (minutes - DAY_START_HOUR * 60) / SLOT_MIN;
}

function slotsBetween(a, b) {
    return (new Date(b).getTime() - new Date(a).getTime()) / (SLOT_MIN * 60 * 1000);
}

const STATUS_RING = {
    scheduled: "border-zinc-300 bg-white dark:border-zinc-700 dark:bg-zinc-900",
    confirmed: "border-sky-400 bg-sky-50 dark:border-sky-700 dark:bg-sky-950/40",
    checked_in: "border-brand-red bg-brand-redSoft text-brand-redDeep dark:border-brand-red dark:bg-brand-red/15",
    converted: "border-emerald-400 bg-emerald-50 dark:border-emerald-700 dark:bg-emerald-950/40",
    cancelled: "border-rose-400 bg-rose-50 line-through opacity-60 dark:border-rose-700 dark:bg-rose-950/40",
    no_show: "border-zinc-300 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-900/40",
};

// Build overlap badges: any pair of appointments in the same row whose intervals overlap
function buildConflictMap(rows, day) {
    const conflicts = new Set();
    for (const row of rows) {
        const items = (row.items || []).filter((a) => a.status !== "cancelled");
        for (let i = 0; i < items.length; i++) {
            for (let j = i + 1; j < items.length; j++) {
                const a = items[i], b = items[j];
                if (slotsBetween(a.scheduled_start, b.scheduled_end) > 0 &&
                    slotsBetween(b.scheduled_start, a.scheduled_end) > 0) {
                    conflicts.add(a.id);
                    conflicts.add(b.id);
                }
            }
        }
    }
    return conflicts;
}

// ---------------- component ----------------

export default function SchedulePage() {
    const { permissions } = useAuth();
    const tz = useTenantTz();
    const { formatTime } = useFormatters();
    const canReschedule = permissions.includes("appointment:update");
    const [day, setDay] = useState(() => isoDay(new Date()));
    const dayKeyTz = useMemo(() => tzDayKey(day, tz), [day, tz]);  // tenant-tz YYYY-MM-DD
    const [mode, setMode] = useState("bay"); // bay | tech
    const [bays, setBays] = useState([]);
    const [techs, setTechs] = useState([]);
    const [appts, setAppts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);

    async function loadStatic() {
        const [b, t] = await Promise.all([BayAPI.list({ is_active: true }), TechnicianAPI.list({ is_active: true })]);
        setBays(b.items || []);
        setTechs(t.items || []);
    }
    async function loadAppts(d) {
        setLoading(true);
        try {
            // Day window in the TENANT timezone — never the browser's.
            const dk = tzDayKey(d, tz);
            const [yy, mm, dd] = dk.split("-").map(Number);
            const start = tzWallClockToUtc(yy, mm, dd, 0, 0, tz).toISOString();
            const end = tzWallClockToUtc(yy, mm, dd, 23, 59, tz).toISOString();
            const r = await AppointmentAPI.list({ start_from: start, start_to: end, page_size: 300 });
            setAppts(r.items || []);
        } catch (e) {
            toast.error(extractError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { loadStatic(); }, []);
    useEffect(() => { loadAppts(day); /* eslint-disable-next-line */ }, [day, tz]);

    const rows = useMemo(() => {
        if (mode === "bay") {
            const buckets = bays.map((b) => ({
                key: b.id,
                label: b.code,
                sublabel: `${b.bay_type} · ${b.name}`,
                items: appts.filter((a) => a.bay_id === b.id),
            }));
            const unassigned = appts.filter((a) => !a.bay_id);
            buckets.unshift({ key: "__unassigned__", label: "Unassigned", sublabel: "No bay", items: unassigned, dropDisabled: true });
            return buckets;
        }
        return techs.map((t) => ({
            key: t.user_id,
            label: t.user?.name || t.employee_code,
            sublabel: (t.skills || []).slice(0, 3).join(" · "),
            items: appts.filter((a) => (a.assigned_technician_user_ids || []).includes(t.user_id)),
        }));
    }, [mode, bays, techs, appts]);

    const conflicts = useMemo(() => buildConflictMap(rows, day), [rows, day]);

    async function moveAppointment({ appt, newRowKey, newSlotIdx }) {
        // 08:00 in tenant tz on the visible day, then shift by drag-snapped slot.
        const [yy, mm, dd] = dayKeyTz.split("-").map(Number);
        const baseUtc = tzWallClockToUtc(yy, mm, dd, DAY_START_HOUR, 0, tz);
        const start = new Date(baseUtc.getTime() + newSlotIdx * SLOT_MIN * 60 * 1000);
        const duration = slotsBetween(appt.scheduled_start, appt.scheduled_end) * SLOT_MIN; // in minutes
        const end = new Date(start.getTime() + duration * 60 * 1000);
        const body = {
            scheduled_start: start.toISOString(),
            scheduled_end: end.toISOString(),
            version: appt.version,
            allow_outside_hours: true,
        };
        if (mode === "bay") {
            body.bay_id = newRowKey === "__unassigned__" ? null : newRowKey;
        } else {
            // Tech mode: keep bay, set tech list to just this row's tech
            body.assigned_technician_user_ids = [newRowKey];
        }
        setBusyId(appt.id);
        try {
            await AppointmentAPI.reschedule(appt.id, body);
            toast.success("Appointment moved");
            await loadAppts(day);
        } catch (e) {
            toast.error(extractError(e));
        } finally { setBusyId(null); }
    }

    return (
        <div className="space-y-5 animate-fade-in" data-testid="schedule-page">
            <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-3xl font-semibold tracking-tight sm:text-4xl">Schedule</h1>
                    <p className="mt-1 text-sm text-zinc-500">Drag bookings between bays / technicians. Saves snap to 15-min steps. Conflicts are flagged in <span className="font-semibold text-brand-redDeep">red</span>.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/appointments/new">
                        <Button data-testid="sched-new-appt"><Plus className="mr-1 h-4 w-4" /> New appointment</Button>
                    </Link>
                </div>
            </header>

            <Card className="p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="icon" onClick={() => setDay((d) => addDays(d, -1))} data-testid="sched-prev"><ChevronLeft className="h-4 w-4" /></Button>
                        <Input
                            type="date"
                            value={dayKeyTz}
                            onChange={(e) => setDay(isoDay(new Date(e.target.value)))}
                            className="w-44"
                            data-testid="sched-date"
                        />
                        <Button variant="outline" size="icon" onClick={() => setDay((d) => addDays(d, 1))} data-testid="sched-next"><ChevronRight className="h-4 w-4" /></Button>
                        <Button variant="ghost" onClick={() => setDay(isoDay(new Date()))} data-testid="sched-today">Today</Button>
                        <span className="ml-2 text-sm font-medium">{dayLabelTz(day, tz)}</span>
                    </div>
                    <Tabs value={mode} onValueChange={setMode}>
                        <TabsList>
                            <TabsTrigger value="bay" data-testid="sched-mode-bay"><ParkingCircle className="mr-1 h-3.5 w-3.5" /> By bay</TabsTrigger>
                            <TabsTrigger value="tech" data-testid="sched-mode-tech"><Wrench className="mr-1 h-3.5 w-3.5" /> By technician</TabsTrigger>
                        </TabsList>
                    </Tabs>
                </div>
            </Card>

            {loading ? (
                <Card className="grid place-items-center p-12 text-sm text-zinc-500"><Loader2 className="h-5 w-5 animate-spin" /></Card>
            ) : rows.length === 0 ? (
                <Card className="grid place-items-center p-12 text-sm text-zinc-500">
                    {mode === "bay" ? "No bays configured yet. Add bays first." : "No technicians available."}
                </Card>
            ) : (
                <ScheduleGrid
                    rows={rows}
                    dayKeyTz={dayKeyTz}
                    tz={tz}
                    formatTime={formatTime}
                    canDrag={canReschedule}
                    onMove={moveAppointment}
                    conflictIds={conflicts}
                    busyId={busyId}
                />
            )}
        </div>
    );
}

// ---------------- grid ----------------

function ScheduleGrid({ rows, dayKeyTz, tz, formatTime, canDrag, onMove, conflictIds, busyId }) {
    const scrollerRef = useRef(null);
    return (
        <Card className="overflow-hidden">
            <div className="overflow-auto" ref={scrollerRef} data-testid="schedule-grid">
                <div className="min-w-[1180px]">
                    {/* Hour ruler */}
                    <div className="flex border-b bg-zinc-50 dark:bg-zinc-900">
                        <div className="w-48 shrink-0 border-r px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500">Track</div>
                        <div className="flex-1 relative" style={{ minHeight: 28 }}>
                            <div className="flex">
                                {Array.from({ length: HOURS + 1 }).map((_, i) => (
                                    <div key={i} className="flex-1 border-r last:border-r-0 px-1 py-1 text-[10px] font-mono uppercase tracking-wider text-zinc-500" style={{ minWidth: PX_PER_SLOT * SLOTS_PER_HOUR }}>
                                        {String(DAY_START_HOUR + i).padStart(2, "0")}:00
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Rows */}
                    {rows.map((row) => (
                        <RowTrack
                            key={row.key}
                            row={row}
                            dayKeyTz={dayKeyTz}
                            tz={tz}
                            formatTime={formatTime}
                            canDrag={canDrag}
                            onMove={onMove}
                            conflictIds={conflictIds}
                            busyId={busyId}
                        />
                    ))}
                </div>
            </div>
        </Card>
    );
}

function RowTrack({ row, dayKeyTz, tz, formatTime, canDrag, onMove, conflictIds, busyId }) {
    const trackRef = useRef(null);
    const [dragHover, setDragHover] = useState(null); // hovered slot index

    function dropHandler(e) {
        e.preventDefault();
        setDragHover(null);
        const data = e.dataTransfer.getData("application/x-appointment");
        if (!data) return;
        let payload;
        try { payload = JSON.parse(data); } catch { return; }
        const slot = pointerToSlot(e, trackRef.current, payload.grabOffsetSlots || 0);
        if (slot == null) return;
        onMove({ appt: payload.appt, newRowKey: row.key, newSlotIdx: slot });
    }

    function pointerToSlot(e, el, offset) {
        if (!el) return 0;
        const rect = el.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const slotIdx = Math.round((x / PX_PER_SLOT) - offset);
        return Math.max(0, Math.min(TOTAL_SLOTS - 1, slotIdx));
    }

    return (
        <div className="flex border-b last:border-b-0" data-testid={`sched-row-${row.key}`}>
            <div className="w-48 shrink-0 border-r bg-zinc-50/60 px-3 py-2 dark:bg-zinc-900/60">
                <div className="text-sm font-semibold truncate font-mono">{row.label}</div>
                <div className="text-[11px] text-zinc-500 truncate">{row.sublabel}</div>
            </div>
            <div
                ref={trackRef}
                className={cn(
                    "relative flex-1",
                    row.dropDisabled ? "bg-zinc-50/40 dark:bg-zinc-900/40" : ""
                )}
                style={{ height: 64, minWidth: PX_PER_SLOT * TOTAL_SLOTS }}
                onDragOver={(e) => {
                    if (!canDrag || row.dropDisabled) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    const data = e.dataTransfer.types.includes("application/x-appointment");
                    if (!data) return;
                    setDragHover(pointerToSlot(e, trackRef.current, 0));
                }}
                onDragLeave={() => setDragHover(null)}
                onDrop={canDrag && !row.dropDisabled ? dropHandler : undefined}
            >
                {/* gridlines (every hour) */}
                <div className="absolute inset-0 pointer-events-none">
                    {Array.from({ length: HOURS + 1 }).map((_, i) => (
                        <div key={i} className="absolute top-0 bottom-0 border-l border-zinc-100 dark:border-zinc-800/70" style={{ left: i * PX_PER_SLOT * SLOTS_PER_HOUR }} />
                    ))}
                </div>
                {/* half-hour subgrid (lighter) */}
                <div className="absolute inset-0 pointer-events-none opacity-50">
                    {Array.from({ length: HOURS * 2 }).map((_, i) => (
                        <div key={i} className="absolute top-0 bottom-0 border-l border-dashed border-zinc-100 dark:border-zinc-900" style={{ left: (i + 0.5) * PX_PER_SLOT * SLOTS_PER_HOUR / SLOTS_PER_HOUR * 2 }} />
                    ))}
                </div>

                {/* drag preview */}
                {dragHover != null && (
                    <div className="pointer-events-none absolute top-1 bottom-1 rounded-md border-2 border-dashed border-brand-red/60 bg-brand-red/10"
                        style={{ left: dragHover * PX_PER_SLOT, width: PX_PER_SLOT * 4 }} />
                )}

                {/* appointment cards */}
                {(row.items || []).map((a) => (
                    <ApptCard
                        key={a.id}
                        appt={a}
                        dayKeyTz={dayKeyTz}
                        tz={tz}
                        formatTime={formatTime}
                        canDrag={canDrag}
                        conflict={conflictIds.has(a.id)}
                        busy={busyId === a.id}
                    />
                ))}
            </div>
        </div>
    );
}

function ApptCard({ appt, dayKeyTz, tz, formatTime, canDrag, conflict, busy }) {
    // Compute slot positions in the TENANT timezone. The previous version used
    // `slotsFromStart(localDate, iso)` which silently re-anchored events to the
    // browser's local clock — see Phase 4.5 timezone defect report.
    const startSlots = slotsFromTzStart(dayKeyTz, appt.scheduled_start, tz);
    const widthSlots = slotsBetween(appt.scheduled_start, appt.scheduled_end);
    if (startSlots == null) return null;

    // Detect events that fall outside the 08:00–20:00 visible band. Previously
    // these were silently dropped (`return null`), which masked the timezone
    // bug entirely. Now we render a tiny clamped "off-band" pill so future
    // drifts surface visibly to the operator.
    const offBefore = startSlots + widthSlots <= 0;
    const offAfter = startSlots >= TOTAL_SLOTS;
    const isOffBand = offBefore || offAfter;
    if (isOffBand) {
        // eslint-disable-next-line no-console
        console.warn(
            "[schedule] appt %s falls outside 08:00–20:00 band in tz %s (startSlots=%s, widthSlots=%s)",
            appt.id, tz, startSlots, widthSlots
        );
    }

    const left = isOffBand
        ? (offBefore ? 0 : (TOTAL_SLOTS - 1) * PX_PER_SLOT)
        : Math.max(0, startSlots) * PX_PER_SLOT;
    const right = isOffBand
        ? left + PX_PER_SLOT
        : Math.min(TOTAL_SLOTS, startSlots + widthSlots) * PX_PER_SLOT;
    const width = Math.max(PX_PER_SLOT, right - left);

    function onDragStart(e) {
        if (!canDrag) { e.preventDefault(); return; }
        const rect = e.currentTarget.getBoundingClientRect();
        const grabOffsetSlots = Math.round((e.clientX - rect.left) / PX_PER_SLOT);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("application/x-appointment", JSON.stringify({ appt, grabOffsetSlots }));
    }

    const cls = STATUS_RING[appt.status] || "border-zinc-300 bg-white";
    return (
        <Link
            to={`/appointments/${appt.id}`}
            draggable={canDrag && appt.status !== "cancelled" && appt.status !== "converted"}
            onDragStart={onDragStart}
            data-testid={`schedule-event-${appt.id}`}
            data-appointment-id={appt.id}
            data-offband={isOffBand ? "true" : "false"}
            className={cn(
                "group absolute top-1 bottom-1 cursor-grab overflow-hidden rounded-md border px-2 py-1 text-[11px] shadow-sm transition-shadow hover:shadow-md active:cursor-grabbing",
                cls,
                conflict && "ring-2 ring-brand-red ring-offset-1",
                busy && "opacity-60",
                isOffBand && "outline-2 outline-dashed outline-brand-red"
            )}
            style={{ left, width }}
            title={`${appt.customer?.display_name || ""} · ${appt.vehicle?.registration_number || ""}${isOffBand ? " · OFF-BAND" : ""}`}
        >
            <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-zinc-600 dark:text-zinc-300">
                <span>{formatTime(appt.scheduled_start)}</span>
                {conflict && (
                    <span
                        data-testid="conflict-badge"
                        style={{ backgroundColor: "rgb(225, 29, 46)", color: "white" }}
                        className="rounded px-1 text-[9px]"
                    >
                        CONFLICT
                    </span>
                )}
            </div>
            <div className="truncate font-semibold">
                {appt.customer?.display_name || "Walk-in"}
            </div>
            <div className="truncate font-mono text-[10px] text-zinc-600 dark:text-zinc-400">
                {appt.vehicle?.registration_number || "—"}
            </div>
        </Link>
    );
}
